import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus, Search } from 'lucide-react';
import { RENEWAL_KINDS, RENEWAL_KIND_LABEL, renewalUrgency } from '@bits/domain';
import type { RenewalKind, RenewalStatusFilter, RenewalSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  RenewalCard,
  RenewalDialog,
  useRenewalQuickActions,
  type RenewalDialogState,
} from '@/components/renewals';

export const renewalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/renewals',
  component: RenewalsPage,
});

const STATUS_FILTERS: ReadonlyArray<{ value: RenewalStatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

const WINDOW_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any expiry' },
  { value: '30', label: 'Due in 30 days' },
  { value: '60', label: 'Due in 60 days' },
  { value: '90', label: 'Due in 90 days' },
];

type LinkFilter = 'all' | 'linked' | 'unlinked';

function hashId(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  return raw || null;
}

function RenewalsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<RenewalStatusFilter>('active');
  const [kind, setKind] = React.useState<RenewalKind | ''>('');
  const [window_, setWindow] = React.useState('');
  const [linkFilter, setLinkFilter] = React.useState<LinkFilter>('all');
  const [clientFilter, setClientFilter] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [dialog, setDialog] = React.useState<RenewalDialogState>(null);
  const [highlightId, setHighlightId] = React.useState<string | null>(hashId);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canEdit = (meQuery.data?.person.roles ?? []).some((r) => r === 'Admin' || r === 'PM');

  const listQuery = useQuery({
    queryKey: ['renewals', status, kind, window_, clientFilter, search],
    queryFn: () =>
      api.listRenewals({
        status,
        kind: kind || undefined,
        withinDays: window_ ? Number(window_) : undefined,
        clientId: clientFilter || undefined,
        search: search.trim() || undefined,
      }),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients-for-renewals'],
    queryFn: () => api.listClients({ status: 'active', page: 1, pageSize: 100 }),
  });
  const totalsQuery = useQuery({
    queryKey: ['renewals', 'active', '', '', ''],
    queryFn: () => api.listRenewals({ status: 'active' }),
  });

  const invalidate = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['renewals'] });
    await queryClient.invalidateQueries({ queryKey: ['renewals-due'] });
    await queryClient.invalidateQueries({ queryKey: ['projects'] });
    await queryClient.invalidateQueries({ queryKey: ['project'] });
  }, [queryClient]);

  const quick = useRenewalQuickActions(invalidate);

  const rows = React.useMemo(() => {
    const all = listQuery.data ?? [];
    if (linkFilter === 'linked') return all.filter((r) => r.projectId !== null);
    if (linkFilter === 'unlinked') return all.filter((r) => r.projectId === null);
    return all;
  }, [listQuery.data, linkFilter]);

  React.useEffect(() => {
    if (!highlightId || !listQuery.data) return;
    const el = document.getElementById(highlightId);
    if (el) el.scrollIntoView({ block: 'center' });
    const t = window.setTimeout(() => setHighlightId(null), 4000);
    return () => window.clearTimeout(t);
  }, [highlightId, listQuery.data]);

  const totals = summarize(totalsQuery.data ?? []);
  const hasFilters =
    kind !== '' ||
    window_ !== '' ||
    linkFilter !== 'all' ||
    clientFilter !== '' ||
    search.trim() !== '';

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Renewals
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Every domain, hosting plan, SSL certificate and AMC the company pays to keep alive, with
            or without a project behind it.
          </p>
        </div>
        {canEdit ? (
          <Button onClick={() => setDialog({ mode: 'add' })}>
            <Plus />
            Add renewal
          </Button>
        ) : null}
      </div>

      {quick.errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{quick.errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Expired"
          value={totals.expired}
          hint="Still active, past their date"
          tone={totals.expired > 0 ? 'bad' : 'neutral'}
          onClick={() => {
            setStatus('active');
            setWindow('30');
          }}
        />
        <StatTile
          label="Due in 30 days"
          value={totals.due30}
          hint="Includes anything expired"
          tone={totals.due30 > 0 ? 'warn' : 'neutral'}
          onClick={() => {
            setStatus('active');
            setWindow('30');
          }}
        />
        <StatTile
          label="Due in 90 days"
          value={totals.due90}
          hint="Plan the budget"
          onClick={() => {
            setStatus('active');
            setWindow('90');
          }}
        />
        <StatTile
          label="Active renewals"
          value={totals.active}
          hint={`${totals.unlinked} not linked to a project`}
          onClick={() => {
            setStatus('active');
            setWindow('');
          }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
          />
          <Input
            className="pl-9"
            placeholder="Search label, provider, client, notes or project…"
            aria-label="Search renewals"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div
          role="tablist"
          aria-label="Status"
          className="flex rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-0.5"
        >
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={status === s.value}
              onClick={() => setStatus(s.value)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm transition-colors',
                status === s.value
                  ? 'bg-[hsl(var(--brand))] text-white'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Select
          aria-label="Filter by type"
          className="h-11 w-auto min-w-[150px]"
          value={kind}
          onChange={(e) => setKind(e.currentTarget.value as RenewalKind | '')}
        >
          <option value="">All types</option>
          {RENEWAL_KINDS.map((k) => (
            <option key={k} value={k}>
              {RENEWAL_KIND_LABEL[k]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by expiry window"
          className="h-11 w-auto min-w-[160px]"
          value={window_}
          onChange={(e) => setWindow(e.currentTarget.value)}
        >
          {WINDOW_OPTIONS.map((w) => (
            <option key={w.value || 'any'} value={w.value}>
              {w.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by project link"
          className="h-11 w-auto min-w-[170px]"
          value={linkFilter}
          onChange={(e) => setLinkFilter(e.currentTarget.value as LinkFilter)}
        >
          <option value="all">Linked or not</option>
          <option value="linked">Linked to a project</option>
          <option value="unlinked">Company-level only</option>
        </Select>
        <Select
          aria-label="Filter by client"
          className="h-11 w-auto min-w-[170px]"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.currentTarget.value)}
        >
          <option value="">All clients</option>
          {(clientsQuery.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.displayName}
            </option>
          ))}
        </Select>
      </div>

      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        {listQuery.isPending
          ? 'Loading renewals…'
          : `${rows.length} ${rows.length === 1 ? 'renewal' : 'renewals'}${
              status === 'all' ? '' : ` · ${status}`
            }`}
      </p>

      {listQuery.isPending ? (
        <GridSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          title={hasFilters ? 'Nothing matches' : 'No renewals yet'}
          body={
            hasFilters
              ? 'Try a different search or clear the filters.'
              : 'Add the company domain, hosting plans, SSL certificates and AMCs so Admins are reminded before they lapse.'
          }
          action={
            hasFilters ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setKind('');
                  setWindow('');
                  setLinkFilter('all');
                  setClientFilter('');
                  setSearch('');
                }}
              >
                Clear filters
              </Button>
            ) : canEdit ? (
              <Button onClick={() => setDialog({ mode: 'add' })}>
                <Plus />
                Add renewal
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {rows.map((r) => (
            <li key={r.id}>
              <RenewalCard
                renewal={r}
                canEdit={canEdit}
                busy={quick.busy}
                showProject
                highlighted={highlightId === r.id}
                onEdit={() => setDialog({ mode: 'edit', renewal: r })}
                onRenewed={r.archivedAt === null ? () => quick.renewed(r) : undefined}
                onArchive={r.archivedAt === null ? () => quick.archive(r) : undefined}
                onRestore={r.archivedAt !== null ? () => quick.restore(r) : undefined}
              />
            </li>
          ))}
        </ul>
      )}

      <RenewalDialog
        state={dialog}
        onClose={() => setDialog(null)}
        onSaved={async () => {
          setDialog(null);
          quick.clearError();
          await invalidate();
        }}
      />
    </AppShell>
  );
}

function summarize(active: RenewalSummary[]) {
  let expired = 0;
  let due30 = 0;
  let due90 = 0;
  let unlinked = 0;
  for (const r of active) {
    if (r.projectId === null) unlinked++;
    if (r.daysLeft === null) continue;
    const u = renewalUrgency(r.daysLeft);
    if (u === 'expired') expired++;
    if (r.daysLeft <= 30) due30++;
    if (r.daysLeft <= 90) due90++;
  }
  return { active: active.length, expired, due30, due90, unlinked };
}

function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  onClick,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: 'neutral' | 'warn' | 'bad';
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[18px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--background))]"
    >
      <Card className="h-full transition-transform hover:-translate-y-0.5">
        <CardContent className="flex flex-col gap-1 p-5">
          <span className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            {label}
          </span>
          <span
            className={cn(
              'font-display text-3xl font-semibold tabular-nums',
              tone === 'bad' && 'text-[hsl(var(--destructive))]',
              tone === 'warn' && 'text-amber-700',
            )}
          >
            {value}
          </span>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</span>
        </CardContent>
      </Card>
    </button>
  );
}

function GridSkeleton(): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3" aria-busy>
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          className="flex min-h-[220px] flex-col gap-4 rounded-[18px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6"
        >
          <div className="flex items-center gap-3">
            <div className="size-10 animate-pulse rounded-xl bg-[hsl(var(--muted))]" />
            <div className="flex flex-1 flex-col gap-2">
              <div className="h-3.5 w-1/2 animate-pulse rounded bg-[hsl(var(--muted))]" />
              <div className="h-3 w-1/3 animate-pulse rounded bg-[hsl(var(--muted))]" />
            </div>
          </div>
          <div className="h-3 w-2/3 animate-pulse rounded bg-[hsl(var(--muted))]" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-[hsl(var(--muted))]" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[hsl(var(--border))] px-8 py-16 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
        <CalendarClock className="size-5" aria-hidden />
      </span>
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        <p className="max-w-md text-sm text-[hsl(var(--muted-foreground))]">{body}</p>
      </div>
      {action}
    </div>
  );
}
